<template>
  <div ref="dashboardContainer"></div>
</template>

<script>
import HeicToJpeg from "@/plugins/uppyHeicToJpeg";
import Uppy from "@uppy/core";
import Dashboard from "@uppy/dashboard";
import { nativeCameraOptions } from "@/utils/uppyCamera";
import Russian from "@uppy/locales/lib/ru_RU";
import English from "@uppy/locales/lib/en_US";
import XHRUpload from "@uppy/xhr-upload";
import { s3PublicUrl } from "@/utils/s3PublicUrl";
import "@uppy/core/css/style.css";
import "@uppy/dashboard/css/style.css";
import { RASTER_IMAGE_TYPES, VIDEO_TYPES } from "@/utils/uploadFileTypes";

// Фото повреждения — по правилу приёма FT-153 REQ-03: видео до 300 МБ, остальное до 50 МБ.
// Сервер (booking_photos#presigned_url) подписывает до 300 МБ. Схема повреждений — PNG до
// 20 МБ, как её подпись booking_damage_images#presigned_url.
const VIDEO_LIMIT = 300 * 1024 * 1024;
const PHOTO_LIMIT = 50 * 1024 * 1024;
const SCHEME_LIMIT = 20 * 1024 * 1024;
// Как в uppy_checkin.vue: по имени allowedFileTypes пропускает из видео только .mov.
const VIDEO_EXTENSION = /\.mov$/i;

export default {
  props: {
    bookingId: {
      type: [Number, String],
      required: true,
    },
    mode: {
      type: String,
      required: true,
      validator: (value) => ['checkin', 'checkout'].includes(value)
    },
    file: {
      type: File,
      required: false,
      default: null
    },
    // Для загрузки фото конкретного повреждения
    bookingDamageId: {
      type: Number,
      required: false,
      default: null
    },
    // Тип загрузки: 'damage_image' (общая схема) или 'damage_photo' (фото конкретного повреждения)
    uploadType: {
      type: String,
      required: false,
      default: 'damage_image',
      validator: (value) => ['damage_image', 'damage_photo'].includes(value)
    }
  },
  data() {
    return {
      // FT-152: источник истины по id записи — карта компонента, чтобы повтор
      // переиспользовал ту же строку, а не плодил пустые.
      photoIdByFile: {},
      uppy: null
    };
  },
  mounted() {
    console.log('=== UppyDamageImage mounted ===');
    console.log('bookingId:', this.bookingId);
    console.log('mode:', this.mode);
    console.log('bookingDamageId:', this.bookingDamageId);
    console.log('uploadType:', this.uploadType);
    
    this.instantiateUppy();
  },
  beforeDestroy() {
    if (this.uppy) {
      this.uppy.destroy();
    }
  },
  methods: {
    // Presign для каждого файла пачки: адрес бакета и поля формы S3 POST policy.
    // Отказ presign — ошибка ЭТОГО файла (upload-error), как было у getUploadParameters:
    // загрузчик пропустит его, кнопка повтора останется.
    async presignFiles(fileIDs) {
      for (const id of fileIDs) {
        const file = this.uppy.getFile(id);
        if (!file) continue;
        try {
          const data = await this.requestPresign(file);
          // Поля формы — в мету (XHRUpload берёт оттуда), но в форму уходят ТОЛЬКО они:
          // поле, которого нет в условиях policy, S3 отбивает. key/bucket/booking_photo_id
          // нужны дальше для подтверждения.
          this.uppy.setFileMeta(id, {
            ...data.fields,
            key: data.key,
            bucket: data.bucket,
            ...(this.uploadType === 'damage_photo'
              ? { booking_photo_id: data.booking_photo_id }
              : {}),
          });
          this.uppy.setFileState(id, {
            xhrUpload: {
              endpoint: data.presigned_url,
              allowedMetaFields: Object.keys(data.fields),
            },
          });
          // FT-152 REQ-04: повтор переиспользует ту же запись.
          if (this.uploadType === 'damage_photo') {
            this.photoIdByFile[id] = data.booking_photo_id;
          }
        } catch (error) {
          console.error('Error in presignFiles:', error);
          this.uppy.emit('upload-error', this.uppy.getFile(id), error);
        }
      }
    },
    async requestPresign(file) {
      // Определяем эндпоинт в зависимости от типа загрузки
      const endpoint = this.uploadType === 'damage_photo'
        ? `booking_photos/${this.bookingId}/presigned_url`
        : `booking_damage_images/${this.bookingId}/presigned_url`;

      const baseUrl = `${this.$store.getters.getApiUrl}/api/v1`; // FT-018

      const requestBody = this.uploadType === 'damage_photo'
        ? {
            filename: file.name,
            content_type: file.type,
            checkin: this.mode === 'checkin',
            booking_damage_id: this.bookingDamageId,
            // FT-152 REQ-04: повтор переиспользует ту же запись.
            booking_photo_id: this.photoIdByFile[file.id]
          }
        : {
            filename: file.name,
            content_type: file.type,
            size: file.size,
            mode: this.mode
          };

      const response = await fetch(`${baseUrl}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': this.$store.getters.getAuthToken
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Error getting presigned URL:', data);
        throw new Error(data.error || 'Failed to get presigned URL');
      }
      return data;
    },
    instantiateUppy() {
      const isDamagePhoto = this.uploadType === 'damage_photo';
      const allowedFileTypes = isDamagePhoto
        ? [...RASTER_IMAGE_TYPES, ...VIDEO_TYPES]
        : ['image/png'];
      
      const note = this.uploadType === 'damage_photo'
        ? (this.$i18n.locale === "ru" ? 'Загрузка фото повреждения...' : 'Uploading damage photo...')
        : (this.$i18n.locale === "ru" ? 'Загрузка изображения повреждений...' : 'Uploading damage image...');

      this.uppy = new Uppy({
        debug: false,
        locale: this.$i18n.locale === "ru" ? Russian : English,
        autoProceed: true,
        allowMultipleUploadBatches: false,
        onBeforeFileAdded: (file) => {
          if (!isDamagePhoto) return true;

          const isVideo = (file.type || '').startsWith('video/')
            || VIDEO_EXTENSION.test(file.name || '');

          if (!isVideo && file.size > PHOTO_LIMIT) {
            this.uppy.info(this.$i18n.locale === 'ru'
              ? 'Фото больше 50 МБ — загрузить нельзя'
              : 'Photos over 50 MB cannot be uploaded', 'error', 5000);
            return false;
          }

          // Как в uppy_checkin.vue: для .mov браузер может отдать application/octet-stream,
          // а маркер повреждения решает «видео ли это» по content_type.
          if (isVideo && !(file.type || '').startsWith('video/')) {
            return { ...file, type: 'video/quicktime' };
          }

          return true;
        },
        restrictions: {
          maxFileSize: isDamagePhoto ? VIDEO_LIMIT : SCHEME_LIMIT,
          maxNumberOfFiles: 1,
          allowedFileTypes: allowedFileTypes
        }
      })
      .use(HeicToJpeg, { quality: 0.92 })
      .use(Dashboard, {
        ...nativeCameraOptions(),
        target: this.$refs.dashboardContainer,
        hideUploadButton: true,
        theme: this.$vuetify.theme.dark ? 'dark' : 'light',
        inline: true,
        width: '100%',
        height: this.uploadType === 'damage_photo' ? 350 : 200,
        note: note,
        proudlyDisplayPoweredByUppy: false
      })
      .use(XHRUpload, {
        // Одним POST по presigned-форме нашего api, а не частями через companion. В Uppy 6
        // `getUploadParameters` у @uppy/aws-s3 удалён, поэтому форму собирает XHRUpload:
        // адрес и поля presign кладёт в сам файл препроцессор presignFiles ниже. Ручка
        // presigned_url та же, что у старых приложений, — её ответ не меняется (R-002).
        formData: true,
        fieldName: 'file',
        limit: 1,
        // S3 на успешный POST формы отвечает 204 с пустым телом, а XHRUpload по
        // умолчанию ждёт JSON.
        getResponseData: () => ({}),
      });

      this.uppy.addPreProcessor(this.presignFiles);

      this.uppy.on('upload-success', async (file, response) => {
        if (!file.meta || !file.meta.key) {
          console.error('Missing metadata:', file.meta);
          return;
        }

        // FT-007: база — бакет ячейки из ответа входа (s3PublicUrl), а не хост Yandex с именем бакета:
        // в EU-ячейке бакет rentprog-eu на Scaleway, и склейка с Yandex давала битое превью.
        const imageUrl = s3PublicUrl(file.meta.key);

        // FT-152: исход подтверждения сначала становится состоянием ФАЙЛА и только потом
        // уходит наружу.
        //
        // Порядок здесь не косметика. Наверху (CarDamageMarker) на upload-error висит
        // обработчик, который раньше сносил диалог вместе с самим загрузчиком, — то есть
        // уничтожал кнопку повтора прежде, чем сотрудник успевал её увидеть. И наружу
        // отдаёт РОВНО ОДИН путь: общий this.uppy.on('upload-error') ниже. Пока событие
        // дублировалось прямым $emit, на каждый отказ приходило по два тоста.
        const failFile = (reason) => {
          console.error('Error confirming upload:', reason);
          this.uppy.emit('upload-error', file,
            new Error(this.$t('bookings.photo_confirm_failed')));
        };

        let payload = null;

        try {
          if (this.uploadType === 'damage_photo') {
            // Подтверждение загрузки фото повреждения
            const booking_photo_id = file.meta.booking_photo_id;

            const confirmResponse = await fetch(
              `${this.$store.getters.getApiUrl}/api/v1/booking_photos/${booking_photo_id}/confirm_upload`, // FT-018
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': this.$store.getters.getAuthToken
                },
                body: JSON.stringify({
                  key: file.meta.key,
                  filename: file.name,
                  size: file.size,
                  content_type: file.type
                })
              }
            );

            const confirmData = await confirmResponse.json();

            if (!confirmResponse.ok) {
              failFile(confirmData);
              return;
            }

            payload = {
              booking_photo_id: booking_photo_id,
              image_url: imageUrl,
              content_type: file.type,
              photo_data: confirmData
            };
          } else {
            // Подтверждение загрузки общей схемы повреждений
            const confirmResponse = await fetch(
              `${this.$store.getters.getApiUrl}/api/v1/booking_damage_images/${this.bookingId}/confirm_upload`, // FT-018
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': this.$store.getters.getAuthToken
                },
                body: JSON.stringify({
                  mode: this.mode,
                  image_url: imageUrl
                })
              }
            );

            const confirmData = await confirmResponse.json();

            if (!confirmResponse.ok) {
              failFile(confirmData);
              return;
            }

            payload = {
              image_url: imageUrl,
              mode: this.mode
            };
          }
        } catch (error) {
          // Сеть оборвалась или ответ не разобрался. Раньше эта ветка только сообщала
          // наружу и файл не помечала вовсе: загрузка выглядела успешной, строка в базе
          // оставалась пустой — самый тихий из отказов.
          failFile(error);
          return;
        }

        // Наружу — уже за пределами try, чтобы отказ обработчика родителя не выдал
        // доставленную фотографию за упавшую.
        this.$emit('upload-success', payload);
      });

      this.uppy.on('upload-error', (file, error, response) => {
        console.error('Upload error:', {
          file: file.name,
          error: error.message || error,
          response: response || 'No response'
        });
        this.$emit('upload-error', error);
      });

      // Добавляем файл в uppy, если он передан
      if (this.file) {
        this.uppy.addFile({
          name: this.file.name,
          type: this.file.type,
          data: this.file,
          source: 'Local',
          isRemote: false
        });
      }
    }
  }
};
</script>

<style scoped>
</style>

